import { useEffect, useRef, useState } from "react";
import { fmtMYT, fmtMYTDate } from "@/lib/myt";
import { ImagePlus, Newspaper, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { title: "", content: "", image_url: "", status: "draft" };

const shortId = (id) => (id || "").slice(-6);

function StatusChip({ status }) {
  return (
    <span className={`text-[10px] font-mono tracking-widest uppercase px-2 py-0.5 rounded-full border ${status === "published" ? "text-emerald-400 border-emerald-500/30" : "text-[#94A3B8] border-[#222530]"}`}>
      {status}
    </span>
  );
}

function ImagePicker({ value, onPick, uploading, inputRef, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
        Featured Image <span className="text-[#64748B]">(optional)</span>
      </label>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }} data-testid={`${testid}-file-input`} />
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="btn-gold-outline px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-50" data-testid={`${testid}-upload-button`}>
          <ImagePlus className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload Image"}
        </button>
        {value && <img src={`${API}${value}`} alt="Featured" className="h-12 w-12 object-cover rounded-md border border-[#D4AF37]/40" data-testid={`${testid}-preview`} />}
      </div>
    </div>
  );
}

export default function AdminNews() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editError, setEditError] = useState("");
  const createFileRef = useRef(null);
  const editFileRef = useRef(null);

  const load = () => {
    api.get("/admin/news").then((res) => setItems(res.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const uploadImage = async (file, apply) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/uploads/image", fd);
      apply(res.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setUploading(false);
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/news", { ...form, image_url: form.image_url || null });
      toast.success(form.status === "published" ? "Announcement published" : "Draft saved");
      setForm(EMPTY);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
    setBusy(false);
  };

  const saveEdit = async () => {
    setBusy(true);
    setEditError("");
    try {
      await api.put(`/admin/news/${editItem.id}`, {
        title: editItem.title, content: editItem.content,
        image_url: editItem.image_url || null, status: editItem.status,
      });
      toast.success("Announcement updated");
      setEditItem(null);
      load();
    } catch (err) {
      setEditError(formatApiError(err));
    }
    setBusy(false);
  };

  const toggle = async (n) => {
    try {
      await api.put(`/admin/news/${n.id}`, {
        title: n.title, content: n.content, image_url: n.image_url,
        status: n.status === "published" ? "draft" : "published",
      });
      toast.success(n.status === "published" ? "Unpublished — hidden from members and IBs" : "Published");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete "${n.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/news/${n.id}`);
      toast.success("Announcement deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="admin-news-page">
      <div>
        <p className="eyebrow-label">Administration</p>
        <h1 className="page-title mt-2">News Management</h1>
        <p className="page-sub mt-1">Create and publish announcements for members and IBs.</p>
      </div>

      <form onSubmit={create} className="card-lux p-6 space-y-4" data-testid="news-create-form">
        <p className="text-sm font-semibold text-white flex items-center gap-2"><Newspaper className="h-4 w-4 text-[#D4AF37]" /> New Announcement</p>
        <div>
          <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Title</label>
          <input className="input-lux" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Announcement title" data-testid="news-title-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Content</label>
          <textarea className="input-lux min-h-[120px]" value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} placeholder="Full announcement message…" data-testid="news-content-input" />
        </div>
        <ImagePicker value={form.image_url} onPick={(file) => uploadImage(file, (url) => setForm((f) => ({ ...f, image_url: url })))} uploading={uploading} inputRef={createFileRef} testid="news-image" />
        <div>
          <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Status</label>
          <select className="input-lux" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} data-testid="news-status-select">
            <option value="draft">Draft (hidden)</option>
            <option value="published">Published (visible to members & IBs)</option>
          </select>
        </div>
        {error && <p className="text-xs text-red-400" data-testid="news-create-error">{error}</p>}
        <button type="submit" disabled={busy || uploading} className="btn-gold px-6 py-2.5 text-xs disabled:opacity-50" data-testid="news-create-button">
          {busy ? "Saving…" : form.status === "published" ? "Publish Announcement" : "Save Draft"}
        </button>
      </form>

      <div className="space-y-3" data-testid="news-admin-list">
        {items.length === 0 ? (
          <p className="text-[#64748B] text-sm" data-testid="news-admin-empty">No announcements yet.</p>
        ) : (
          items.map((n) => (
            <div key={n.id} className="card-lux p-5 flex items-center gap-4" data-testid={`news-item-${shortId(n.id)}`}>
              {n.image_url && <img src={`${API}${n.image_url}`} alt="" className="h-12 w-12 object-cover rounded-md border border-[#222530] shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{n.title}</p>
                <p className="text-[11px] text-[#64748B] font-mono mt-0.5">
                  {n.status === "published" && n.published_at
                    ? `Published ${fmtMYT(n.published_at)}`
                    : `Created ${n.created_at ? fmtMYT(n.created_at) : ""}`}
                </p>
              </div>
              <StatusChip status={n.status} />
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => toggle(n)} className="btn-gold-outline px-3.5 py-1.5 text-[11px]" data-testid={`news-toggle-${shortId(n.id)}`}>
                  {n.status === "published" ? "Unpublish" : "Publish"}
                </button>
                <button onClick={() => { setEditError(""); setEditItem({ ...n }); }} className="p-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors" data-testid={`news-edit-${shortId(n.id)}`} aria-label="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(n)} className="p-2 text-[#94A3B8] hover:text-red-400 transition-colors" data-testid={`news-delete-${shortId(n.id)}`} aria-label="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {editItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setEditItem(null)} />
          <div className="relative card-lux w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 space-y-4" data-testid="news-edit-modal">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-white">Edit Announcement</p>
              <button onClick={() => setEditItem(null)} className="text-[#94A3B8] hover:text-white p-1" data-testid="news-edit-cancel-button" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Title</label>
              <input className="input-lux" value={editItem.title} onChange={(e) => setEditItem((m) => ({ ...m, title: e.target.value }))} data-testid="news-edit-title-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Content</label>
              <textarea className="input-lux min-h-[120px]" value={editItem.content} onChange={(e) => setEditItem((m) => ({ ...m, content: e.target.value }))} data-testid="news-edit-content-input" />
            </div>
            <ImagePicker value={editItem.image_url} onPick={(file) => uploadImage(file, (url) => setEditItem((m) => ({ ...m, image_url: url })))} uploading={uploading} inputRef={editFileRef} testid="news-edit-image" />
            <div>
              <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Status</label>
              <select className="input-lux" value={editItem.status} onChange={(e) => setEditItem((m) => ({ ...m, status: e.target.value }))} data-testid="news-edit-status-select">
                <option value="draft">Draft (hidden)</option>
                <option value="published">Published (visible to members & IBs)</option>
              </select>
            </div>
            {editError && <p className="text-xs text-red-400" data-testid="news-edit-error">{editError}</p>}
            <button onClick={saveEdit} disabled={busy || uploading} className="btn-gold px-6 py-2.5 text-xs w-full disabled:opacity-50" data-testid="news-edit-save-button">
              {busy ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
